<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import PasswordForm from '@/components/account/password-form.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { useUserInfo } from '@/utils/swr';

const { createSnackbar } = useSnackbar();
const route = useRoute();
const { data: userInfo } = useUserInfo();
const timesChanged = ref(0); // 保存してフォームをリセットするためにkeyとして使われている
const currentPassword = ref('');
const newPassword = ref('');
const isNewPasswordValid = ref(false);
const isNewPasswordDifferent = computed(
  () => currentPassword.value !== newPassword.value,
);

const newPasswordUpdated = ({
  password,
  isValid,
}: {
  password: string;
  isValid: boolean;
}) => {
  newPassword.value = password;
  isNewPasswordValid.value = isValid;
};

const canSubmit = computed(
  () =>
    currentPassword.value.length > 0 &&
    isNewPasswordValid.value &&
    isNewPasswordDifferent.value,
);

const submit = async () => {
  try {
    await api.changePassword(currentPassword.value, newPassword.value, {
      pageName: 'password',
      pageUrl: route.fullPath,
    });

    createSnackbar({
      message: 'パスワードを変更しました',
      type: 'success',
    });
    currentPassword.value = '';
    newPassword.value = '';
    isNewPasswordValid.value = false;
    timesChanged.value += 1;
  } catch {
    // パスワードが変更できないことは現在のパスワードが正しくない可能性がある
    createSnackbar({
      message:
        'パスワードを変更できませんでした。現在のパスワードを再入力してください。',
      type: 'error',
    });
  }
};
</script>
<template>
  <div class="user-settings" v-if="userInfo">
    <Header title="パスワード" header-width="min(912px, 100%)" />
    <div class="settings-body">
      <div class="settings-card">
        <div class="c-formBlock">
          <div class="c-formBlock__label">現在のパスワード</div>
          <input
            class="c-textInput"
            type="password"
            placeholder=""
            v-model="currentPassword"
          />
          <div class="spacing-04"></div>
          <router-link
            class="forgot-password c-text-regular--m"
            :to="{ name: 'forgotPassword' }"
            >パスワードを忘れてしまった場合</router-link
          >
        </div>
        <div class="spacing-12"></div>
        <div class="c-formBlock">
          <PasswordForm
            :key="timesChanged"
            @password-changed="newPasswordUpdated"
          ></PasswordForm>
          <div
            class="c-formBlock__text c-formBlock__text--error"
            v-if="
              currentPassword.length > 0 &&
              isNewPasswordValid &&
              !isNewPasswordDifferent
            "
          >
            <div class="c-text-regular--s">
              現在のパスワードと変わらないため、パスワードを変更できません。
            </div>
            <div class="c-text-regular--s">
              再度、新しいパスワードを入力してください。
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="settings-footer">
      <div class="settings-footer-inner">
        <button
          class="c-btn c-btn--small c-btn--AnewsPrimary"
          :class="{ disabled: !canSubmit }"
          :disabled="!canSubmit"
          @click="submit"
        >
          保存
        </button>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);

  .settings-footer-inner {
    max-width: 912px;
    margin: 0 auto;
  }

  .c-btn {
    width: 64px;
    margin: 0;
  }
}

.user-settings {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;

  .settings-body {
    box-sizing: border-box;
    width: 100%;
    max-width: calc(912px + 48px);
    margin: 0 auto;
    padding: 24px;
  }

  .settings-card {
    box-sizing: border-box;
    padding: 16px;
    background: #fff;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .forgot-password {
      color: $color-green600;
    }

    .o-password-form {
      flex-direction: column;
    }

    .c-formBlock {
      max-width: 320px;
    }

    .c-formBlock div {
      flex-direction: column;
    }
  }
}
</style>
